/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import styles from './QuickStartContent.module.scss';

export default function QuickStartContent({
  handleDialog,
  isCreatorMode,
}: {
  handleDialog: () => void;
  isCreatorMode: boolean;
}) {
  return (
    <Dialog
      data-testid="quickstart-guide-dialog"
      open={true}
      onClose={handleDialog}
      PaperProps={{ sx: { borderRadius: 0, border: '2px solid var(--blue)', backgroundColor: 'var(--white)' } }}
    >
      <DialogTitle style={{ fontSize: 22, color: 'var(--black)' }}>Quick start guide</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleDialog}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
        }}
      >
        <CloseIcon style={{ color: 'var(--blue)' }} />
      </IconButton>{' '}
      <div className={styles.content} aria-labelledby="quickstartguide">
        {' '}
        {!isCreatorMode ? (
          <div id="consumer-quickstartguide">
            <div>
              <h4>Basic Navigation</h4>
              <p>
                The <strong>left side panel is for navigation</strong>. It is always available while you are logged into
                the application.
              </p>
              <p>
                <strong>Collapse</strong> the side panel for expanded screen size. Click the left-pointing arrow to{' '}
                <strong> tuck or collapse</strong> the left panel. <strong>Expand or untuck </strong> the side panel by
                clicking the <strong>stacked bars</strong>. The stacked bars are commonly known as the hamburger or the
                waffle.
              </p>
              <p>Both the expanded and the collapsed view contain the icons necessary to navigate the application.</p>
              <p>
                Click the <strong>first icon or Knowledge Hub hyperlink</strong> to search for any condition or symptom
                specific.
              </p>
            </div>
            <div className={styles.contentInfo}>
              <h4>General Knowledge Hub Information</h4>
              <p>
                A Knowledge Hub user is granted permissions for use of the tool. Generally speaking, you are granted
                permissions as either a Knowledge Hub creator (an author) or a user (a clinician and consumer of the
                tool). The creator login allows for the creation of a topic specific to a biomedical clinical state. The
                topic, once reviewed and published, supports the clinician in navigating ambiguities in topic treatment
                protocols, diagnostic workups, and referrals, known as Guides.
              </p>
              <p>
                Alternatively, the creator may create an Algorithm or Topic Triage, designed to support the clinician
                through a series of questions and answers to determine the urgency of the need for treatment and the
                nature of the treatment required.
              </p>
            </div>
            <div className={styles.contentInfo}>
              <h4>Access Knowledge Hub Content</h4>
              <p>
                Click the left panel Knowledge Hub hyperlink. A search screen will appear. Type the topic of interest,
                diagnosis, symptom, lab, image or procedure and press enter.
              </p>
            </div>
            <div className={styles.contentInfo}>
              <h4>CME</h4>
              <p>
                Click the CME counter in the left-hand navigation bar to view what topics have been opened and are
                available for CME. Select All or the specific topics you prefer to claim and then follow the prompts to
                answer the required questions and download your certification.
              </p>
            </div>
            <div className={styles.contentInfo}>
              <h4>Favorites</h4>
              <p>
                Click the 2nd icon, shaped as a star, in the left-hand navigation bar to see what topics you may have
                selected. See the same star icon to the right of the topic name in the app to add a topic to the
                Favorites tab. This can help clinicians remember which topics are most useful at the point of care,
                especially those with real time patient data elements.
              </p>
            </div>
          </div>
        ) : (
          <div id="creator-quickstartguide">
            <h4>Basic Navigation</h4>
            <p>
              The <b>left side panel is for navigation</b>. It is always available while you are logged into the
              application.
            </p>
            <p>
              <strong>Collapse</strong> the side panel for mobile-friendly use. Click the left pointing arrow to &#xA0;
              <strong>tuck or collapse</strong> the left panel. <strong> Expand or untuck&#xA0;</strong> the side panel
              by clicking the <strong>stacked bars</strong>. The stacked bars are commonly known as the hamburger or the
              waffle.
            </p>
            <p>Both the expanded and the collapsed view contain the icons necessary to navigate the application.</p>
            <p>
              Click the <b>silhouette of the human</b> for commonly used creator functions such as creating a document
              and its guides.
            </p>
            <p>
              {' '}
              Click the <b>circle</b> for commonly used consumer functions such as identifying diagnostic orders to
              consider prior to referring a patient to a specialist.
            </p>{' '}
            <p>
              {' '}
              Clicking the <b>stacked squares</b> always returns you home.
            </p>{' '}
            <h4>General Knowledge Hub Information</h4>
            <p>
              A Knowledge Hub user is granted permissions for use of the tool. Generally speaking, you are granted
              permissions as either a Knowledge Hub creator (an author) or a consumer (a practitioner and user of the
              tool). The creator login authorizes the privilege to create a document specific to a biomedical clinical
              state, a topic. The document, once reviewed and published, supports the consumer user in navigating
              ambiguities in topic treatment protocols, diagnostic workups, and referrals.
            </p>{' '}
            <p>
              Alternatively, the creator may create a Triage document, another document type which is less specific to a
              topic but is designed to support the consumer through a series of questions and answers to determine the
              urgency of the need for treatment and the nature of the treatment required. The Triage document, once
              isolating a probable cause, may link to an existing document.
            </p>{' '}
            <h4>Create New Document</h4>
            (Required: Creator privileges) Once logged in you will see the <b>human silhouette</b> icon hyperlink, and
            the “<b>Your Documents</b>” text hyperlink. Click the hyperlinks on the left navigation panel or,
            alternatively, click the “<b>New Document</b>” button on the Home screen.
            <p>
              Next the document creation screen will open. Entering the title will activate the "
              <b>Create New Document</b>" button on lower part of the screen. The new document draft, version 1, is
              created after the user clicks the Create New Document button.
            </p>
            <h4>Create New Triage</h4>
            <p>
              (Required: Creator privileges) Creating a Triage document works the same way as creating a regular
              document with a few important distinctions. The Triage document contains no guide tabs. You won’t see the
              Clinical Notes, Orders to Consider and Referral Scheduling Recommendations tabs. A Triage is purely an
              algorithm triggered for viewing by the consumer search term. Follow the same steps as you would for Create
              New Document above.
            </p>
          </div>
        )}
        <Button variant="contained" className={styles.dismissBtn} onClick={handleDialog}>
          Dismiss
        </Button>
      </div>
    </Dialog>
  );
}
